/* XSREZ cart cross-sell.

   Two jobs: fill the row rendered by snippets/xs-cross-sell.liquid with FOUR
   cards, and make its Add buttons update the cart in place.

   FILLING - and why it accumulates rather than picks a winner.

   The snippet hands over a list of candidate URLs, most specific first: the
   anchor product's hand-picked COMPLEMENTS, then the team tag, then the licence
   collections, then a store-wide floor. Each is fetched as `<url>?view=xs-cross`
   with `layout none` - a bare list of <li>, already filtered against the
   shopper's cart server-side. Rung 0 renders through
   templates/product.xs-cross.liquid; every other rung through
   templates/collection.xs-cross.liquid.

   The first build stopped at the first candidate that returned enough. That
   still left short rows whenever a licence was genuinely thin: the template's
   own three passes fill four cards only if four eligible products EXIST in that
   one collection. Harvard Crimson returns exactly one.

   So this walks the ladder and TOPS UP - takes the complements, then what the
   team tag has, then the licence, then the floor - until it holds four cards or
   runs out of candidates. The ladder is ordered closest-match-first, so topping
   up widens the net one step at a time rather than jumping straight to "here is
   something random". A product with two complements still yields a full row of
   four; a product with none still yields a full row of four.

   Cards are deduped by product URL across fragments, because a product in the
   Warriors tag is also in the NBA collection and would otherwise appear twice -
   and a complement is very often in the licence collection too.

   THE HEADING FOLLOWS THE MAJORITY LABEL, not the first card. "More Harvard
   Crimson" over three tiles that are not Harvard is a small lie, and the shopper
   can see it. Every card is attributed to the candidate that supplied it, and
   the heading is only replaced when one label accounts for at least HALF the
   row. Ties go to the earlier rung, which is the more specific one. The floor
   collections carry no label at all, so a row mostly made of floor picks keeps
   the generic heading.

   A LABEL BEGINNING WITH '=' IS USED VERBATIM instead of being prefixed with
   "More". That is how rung 0 reads "Completes the look" rather than the nonsense
   "More Completes the look".

   Why an observer rather than a one-off run: the cart drawer is replaced
   wholesale by the Section Rendering API on every cart change, so the element
   this fills is destroyed and rebuilt repeatedly, and the rebuilt markup can
   never run a script of its own.

   ADDING - and why this file is a MODULE.

   The cards ship a real <form action="/cart/add" method="post"> with
   `return_to=back`, which works with no JavaScript at all. But in the drawer
   that means a full page load: the drawer closes, the shopper lands back on the
   page they were on, and the drawer re-opens. That should not happen - the
   drawer should just refresh.

   Horizon's own add-to-cart does that through Shopify's standard cart event, so
   this does the same thing rather than inventing a parallel mechanism:

     1. collect the section ids from every <cart-items-component data-section-id>
     2. dispatch CartLinesUpdateEvent with a DEFERRED promise, before fetching
     3. POST to the form's own action with `sections` in the payload
     4. re-read /cart.js, then resolve the deferred promise with
        { cart, detail: { items, sections, ... } }

   The theme's own components are what re-render from `detail.sections` - the
   line items, the totals, the free-shipping bar and the cart icon all update
   together, and the dialog is never replaced, so it stays open. Copying that
   contract is the whole reason this works; a hand-rolled innerHTML swap of the
   drawer would drop the quantity steppers' event wiring and fight the theme.

   The import is DYNAMIC and failure is survivable: if `@shopify/events` cannot
   be resolved, CartLinesUpdateEvent stays null, the submit handler never
   preempts, and the plain form post takes over exactly as before. A page reload
   is a worse experience, not a broken one. */

let CartLinesUpdateEvent = null;
let cartLinesUpdateName = 'shopify:cart:lines-update';

try {
  const events = await import('@shopify/events');
  CartLinesUpdateEvent = events.CartLinesUpdateEvent || null;
  if (events.StandardEvents && events.StandardEvents.cartLinesUpdate) {
    cartLinesUpdateName = events.StandardEvents.cartLinesUpdate;
  }
} catch (error) {
  /* Left null on purpose - see the note above. */
}

let cache = {};
let timer = null;

/* Four tiles, always. The drawer grid divides its width by however many cards
   exist, so a short row is not broken - but it is a wasted slot, and the point
   of the row is to give a shopper enough to choose from. */
const WANT = 4;

/* Fetches are sequential, so this bounds the worst case. A one-item cart
   generates about seven candidates including the complements rung and the
   floor; the cap only bites on a cart holding several heavily tagged products. */
const MAX_CANDIDATES = 9;

function parse(value) {
  const out = [];
  const parts = (value || '').split('||');
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part) continue;
    const at = part.indexOf('::');
    if (at < 0) continue;
    out.push({ label: part.slice(0, at), url: part.slice(at + 2) });
  }
  return out;
}

function load(url) {
  if (Object.prototype.hasOwnProperty.call(cache, url)) {
    return Promise.resolve(cache[url]);
  }
  return fetch(url + '?view=xs-cross', { credentials: 'same-origin' })
    .then((res) => (res.ok ? res.text() : ''))
    .catch(() => '')
    .then((text) => {
      const body = (text || '').trim();
      cache[url] = body;
      return body;
    });
}

/* Parsed through a <template> rather than by regex so the cards arrive as real
   nodes: they can be appended straight into the live list, and their product
   URL is read from the DOM instead of scraped out of a string. */
function cardsFrom(html) {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  return tpl.content.querySelectorAll('.xsx__item');
}

function keyOf(card) {
  const link = card.querySelector('.xsx__link');
  const href = link ? link.getAttribute('href') || '' : '';
  return href.split('?')[0];
}

function fill(box) {
  /* Marked before any await, and never unmarked. A candidate list that comes
     back empty everywhere must not be retried on the next mutation, or the
     observer turns into a fetch loop. A cart change does not need it unmarked
     either: the drawer is re-rendered from scratch, so the box that arrives is
     a new element with no flag on it. */
  if (box.getAttribute('data-xsx-done')) return;
  box.setAttribute('data-xsx-done', '1');

  const listEl = box.querySelector('.xsx__list');
  if (!listEl) return;

  const all = parse(box.getAttribute('data-xsx'));
  const seenUrls = {};
  let candidates = [];
  for (let i = 0; i < all.length; i++) {
    if (!seenUrls[all[i].url]) {
      seenUrls[all[i].url] = 1;
      candidates.push(all[i]);
    }
  }
  candidates = candidates.slice(0, MAX_CANDIDATES);

  const picked = [];
  const seenCards = {};

  /* Attribution, so the heading can be checked against the row rather than
     guessed from the first card. labelOrder preserves ladder order, which is
     what breaks a tie in favour of the more specific rung. */
  const labelCount = {};
  const labelOrder = [];
  let at = 0;

  function finish() {
    if (!picked.length) return;
    for (let i = 0; i < picked.length; i++) listEl.appendChild(picked[i]);

    let best = '';
    let bestN = 0;
    for (let i = 0; i < labelOrder.length; i++) {
      const name = labelOrder[i];
      if (labelCount[name] > bestN) {
        best = name;
        bestN = labelCount[name];
      }
    }

    const head = box.querySelector('.xsx__head');
    /* "More Kansas City Chiefs" is a better heading than the generic one, but
       only when the row is actually mostly that. And "More Classic" is never
       right - Classic is a catalogue bucket, not something a shopper
       identifies with. */
    if (head && best && bestN * 2 >= picked.length) {
      if (best.charAt(0) === '=') {
        head.textContent = best.slice(1);
      } else if (best !== 'Classic') {
        head.textContent = 'More ' + best;
      }
    }
    box.hidden = false;
  }

  function step() {
    if (picked.length >= WANT || at >= candidates.length) {
      finish();
      return;
    }
    const candidate = candidates[at++];
    load(candidate.url).then((html) => {
      if (html) {
        const cards = cardsFrom(html);
        for (let i = 0; i < cards.length && picked.length < WANT; i++) {
          const key = keyOf(cards[i]);
          /* A product in the Warriors tag is also in the NBA collection - and a
             complement is very often in the licence collection too. */
          if (!key || seenCards[key]) continue;
          seenCards[key] = 1;
          if (candidate.label) {
            if (!Object.prototype.hasOwnProperty.call(labelCount, candidate.label)) {
              labelCount[candidate.label] = 0;
              labelOrder.push(candidate.label);
            }
            labelCount[candidate.label]++;
          }
          picked.push(cards[i]);
        }
      }
      step();
    });
  }

  step();
}

function scan() {
  const boxes = document.querySelectorAll('.xsx[data-xsx]');
  for (let i = 0; i < boxes.length; i++) fill(boxes[i]);
}

function schedule() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    scan();
  }, 60);
}

/* ------------------------------------------------------------------ adding */

/* Every fragment is filtered against the cart at the moment it was fetched, so
   the moment the cart changes every cached fragment is stale - it may still be
   offering the thing that was just added. Clearing on the cart event covers our
   own adds and the theme's alike: quantity changes and removals fire it too. */
document.addEventListener(cartLinesUpdateName, () => {
  cache = {};
});

function sectionIds() {
  const ids = [];
  const nodes = document.querySelectorAll('cart-items-component[data-section-id]');
  for (let i = 0; i < nodes.length; i++) {
    const id = nodes[i].dataset.sectionId;
    if (id && ids.indexOf(id) < 0) ids.push(id);
  }
  return ids;
}

function addToCart(form, variantId) {
  const button = form.querySelector('.xsx__add');
  const label = button ? button.textContent : '';

  if (form.dataset.xsxBusy) return;
  form.dataset.xsxBusy = '1';
  if (button) {
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding';
  }

  const deferred = CartLinesUpdateEvent.createPromise();

  /* Dispatched from the form, not from document, so it bubbles the way the
     theme's own events do and any listener that inspects event.target sees a
     real element inside the drawer. */
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      lines: [{ quantity: 1, merchandiseId: variantId }],
      promise: deferred.promise,
    })
  );

  const ids = sectionIds();

  fetch(form.getAttribute('action'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: ids.join(','),
    }),
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.status) throw new Error(res.description || res.message || 'Add to cart failed');
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          cache = {};
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'xs-cross-sell',
              sourceId: 'xs-cross-sell',
              itemCount: 1,
              sections: res.sections,
              didError: false,
            },
          });
          /* No cleanup of the button here on purpose: the section this form
             lives in is about to be replaced by the theme's renderer, and the
             replacement is built from the new cart, so this product is already
             filtered out of it. */
        });
    })
    .catch((error) => {
      deferred.reject(error);
      form.dataset.xsxBusy = '';
      if (button) {
        button.removeAttribute('aria-busy');
        button.textContent = label;
      }
      /* Last resort, so a shopper is never stuck looking at a dead button: do
         it the way it worked before this function existed. */
      form.submit();
    });
}

document.addEventListener('submit', (event) => {
  if (!CartLinesUpdateEvent) return;
  const target = event.target;
  if (!target || !target.closest) return;
  const form = target.closest('.xsx__form');
  if (!form) return;

  const input = form.querySelector('input[name="id"]');
  const variantId = input ? Number(input.value) : 0;
  if (!variantId) return;

  /* Nothing to re-render into means nothing to gain from intercepting. */
  if (!sectionIds().length) return;

  event.preventDefault();
  addToCart(form, variantId);
});

/* ------------------------------------------------------------------- start */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scan);
} else {
  scan();
}

if (window.MutationObserver) {
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}